import { fetchWithAuth, getCSRFToken } from '../auth';

const API_HOST = process.env.REACT_APP_ICC_TOOL_API_HOST;

// Always called at request time so the CSRF token is never stale.
const readOpts = () => ({
    headers: { 'X-CSRFToken': getCSRFToken() },
    credentials: 'include',
});

// Builds URLSearchParams from an object, skipping null/undefined/empty values.
// Array values (e.g. multi-select state/market_id filters) are sent comma-separated.
const buildQuery = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        if (Array.isArray(v)) {
            if (v.length > 0) qs.set(k, v.join(','));
            return;
        }
        qs.set(k, v);
    });
    return qs;
};

// Triggers a file download from a Blob response.
function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: filename });
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

// Dedupes identical concurrent calls (e.g. the dashboard's summary fetch and the breakdown
// accordion's root-level fetch both request `level=state` with the same dates on initial
// load) so only one network request goes out; every caller shares its result.
const inFlightReportRequests = new Map();

// Summary + state/market/team drilldown rows, scoped to the current level/state/market.
// `signal` lets callers abort waiting on the (possibly shared) request — e.g. a filter
// change superseding a stale request — without cancelling it for other callers.
export async function getAssetProblemReport(params = {}, signal) {
    const key = buildQuery(params).toString();
    let request = inFlightReportRequests.get(key);
    if (!request) {
        request = fetchWithAuth(`${API_HOST}manager/asset-problem-report/?${key}`, readOpts())
            .then((resp) => {
                if (!resp.ok) throw new Error('Failed to fetch asset problem report.');
                return resp.json();
            })
            .finally(() => inFlightReportRequests.delete(key));
        inFlightReportRequests.set(key, request);
    }
    if (!signal) return request;
    return new Promise((resolve, reject) => {
        const onAbort = () => reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
        if (signal.aborted) {
            onAbort();
            return;
        }
        signal.addEventListener('abort', onAbort, { once: true });
        request.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
    });
}

// Team-level problem rows for the detail/drilldown page.
export async function getAssetProblemReportProblems(params = {}) {
    const resp = await fetchWithAuth(`${API_HOST}manager/asset-problem-report/problems/?${buildQuery(params)}`, readOpts());
    if (!resp.ok) throw new Error('Failed to fetch team asset problems.');
    return resp.json();
}

export async function exportAssetProblemReport(params = {}) {
    const resp = await fetchWithAuth(`${API_HOST}manager/asset-problem-report/export/?${buildQuery(params)}`, readOpts());
    if (!resp.ok) throw new Error('Failed to export asset problem report.');
    const filename = params.view === 'detail' ? 'asset_problem_report_detail.xlsx' : 'asset_problem_report.xlsx';
    downloadBlob(await resp.blob(), filename);
}
